function safeJsonParse(s) {
  if (s == null) return null;
  try {
    return typeof s === "string" ? JSON.parse(s) : s;
  } catch {
    return null;
  }
}

function safeJsonStringify(v) {
  try {
    return JSON.stringify(v);
  } catch {
    return "{}";
  }
}

export function ensureGraph(config) {
  const cfg = config && typeof config === "object" ? { ...config } : {};
  const graph = cfg.graph && typeof cfg.graph === "object" ? { ...cfg.graph } : null;
  if (graph && Array.isArray(graph.nodes) && Array.isArray(graph.edges)) return cfg;

  return {
    ...cfg,
    graph: {
      nodes: [
        { key: "start", type: "Start", position: { x: 0, y: 0 }, config: { label: "Início" } },
        { key: "end", type: "End", position: { x: 420, y: 0 }, config: { label: "Fim" } },
      ],
      edges: [{ from: "start", to: "end", condition: { always: true } }],
    },
  };
}

export function migrateTriggersToGraph(config) {
  const cfg = config && typeof config === "object" ? { ...config } : {};
  const triggers = Array.isArray(cfg.triggers) ? cfg.triggers.map((t) => ({ ...(t || {}) })) : [];
  const t0 = triggers[0] || {};

  const nextCfg = ensureGraph(cfg);
  const graph = nextCfg.graph;
  const nodes = Array.isArray(graph.nodes) ? [...graph.nodes] : [];
  // Remove atalho start→end para a cadeia migrada não ser ignorada pelo executor.
  let edges = (Array.isArray(graph.edges) ? [...graph.edges] : []).filter(
    (e) => !(String(e.from) === "start" && String(e.to) === "end")
  );

  const hasKey = new Set(nodes.map((n) => String(n.key)));
  const addNode = (node) => {
    const key = String(node.key);
    if (hasKey.has(key)) return;
    nodes.push(node);
    hasKey.add(key);
  };
  const addEdge = (from, to, condition = { always: true }) => {
    edges.push({ from: String(from), to: String(to), condition });
  };

  // Build a linear chain: start -> (CreateLead?) -> (Tag?) -> (ChannelMessage?) -> end
  let last = "start";
  let y = 0;

  if (t0.createLead === true) {
    addNode({
      key: "createLead",
      type: "CreateLead",
      position: { x: 220, y: y - 140 },
      config: { label: "Criar lead" },
    });
    addEdge(last, "createLead");
    last = "createLead";
  }

  if (t0.tag) {
    addNode({
      key: "tag",
      type: "Tag",
      position: { x: 220, y: y },
      config: { label: "Tag", tag: String(t0.tag) },
    });
    addEdge(last, "tag");
    last = "tag";
  }

  if (t0.dmText) {
    addNode({
      key: "message",
      type: "ChannelMessage",
      position: { x: 220, y: y + 140 },
      config: { label: "Enviar mensagem", text: String(t0.dmText) },
    });
    addEdge(last, "message");
    last = "message";
  }

  if (last !== "end") {
    addEdge(last, "end");
  }

  // Prevent double execution: when graph exists, backend still runs trigger actions 1-3.
  // So we move those actions into graph and keep triggers only for filtering (eventName/mediaId/keyword).
  const sanitizedTriggers = triggers.map((t) => ({
    eventName: t.eventName,
    mediaId: t.mediaId || "",
    keyword: t.keyword || "",
    // keep metadata used by UI/analytics
    createLead: false,
    tag: "",
    dmText: "",
  }));

  return {
    ...nextCfg,
    triggers: sanitizedTriggers,
    graph: {
      ...graph,
      nodes,
      edges,
    },
    _migratedFromTriggersAt: new Date().toISOString(),
  };
}

export function flowGraphToReactFlow(graph) {
  const g = graph && typeof graph === "object" ? graph : { nodes: [], edges: [] };
  const nodes = Array.isArray(g.nodes) ? g.nodes : [];
  const edges = Array.isArray(g.edges) ? g.edges : [];

  const rfNodes = nodes.map((n) => {
    const conf = safeJsonParse(n.config || n.config_json) || n.config || {};
    const label = conf.label || conf.name || n.key;
    return {
      id: String(n.key),
      type: "xproFlowNode",
      position: n.position && typeof n.position === "object" ? n.position : { x: 0, y: 0 },
      data: {
        key: String(n.key),
        nodeType: String(n.type || n.node_type || "Action"),
        label: String(label),
        rawConfig: conf,
      },
    };
  });

  const rfEdges = edges.map((e, idx) => {
    const cond = safeJsonParse(e.condition || e.condition_json) || e.condition || null;
    const label =
      cond && typeof cond === "object" && cond.keywordContains
        ? `kw: ${String(cond.keywordContains)}`
        : cond && typeof cond === "object" && cond.always
          ? "always"
          : "";
    return {
      id: e.id ? String(e.id) : `e_${String(e.from)}_${String(e.to)}_${idx}`,
      source: String(e.from),
      target: String(e.to),
      type: "smoothstep",
      label,
      data: { condition: cond },
    };
  });

  return { nodes: rfNodes, edges: rfEdges };
}

export function reactFlowToFlowGraph(rfNodes, rfEdges) {
  const nodes = (rfNodes || []).map((n) => {
    const nodeType = n?.data?.nodeType || "Action";
    const rawConfig = n?.data?.rawConfig && typeof n.data.rawConfig === "object" ? n.data.rawConfig : {};
    const label = n?.data?.label != null ? String(n.data.label) : String(n.id);
    const config = { ...rawConfig, label };
    return {
      key: String(n.id),
      type: String(nodeType),
      position: n.position && typeof n.position === "object" ? n.position : { x: 0, y: 0 },
      config_json: safeJsonStringify(config),
    };
  });

  const edges = (rfEdges || []).map((e) => {
    const cond = e?.data?.condition ?? null;
    return {
      from: String(e.source),
      to: String(e.target),
      condition_json: cond ? safeJsonStringify(cond) : null,
    };
  });

  return { nodes, edges };
}

